import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton } from "@/components/brand";
import { useLang, t, withLang } from "@/lib/i18n";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const lang = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    document.title = lang === "fr" ? "Routa · Nous joindre" : "Routa · Contact";
  }, [lang]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setState("busy");
    try {
      await api.post("/contact", form);
      setState("sent");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) ||
        t(lang, { en: "Could not send. Please try again shortly.", fr: "Impossible d'envoyer. Veuillez réessayer sous peu." }));
      setState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="contact-page">
      <MarketingNav />
      <section className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-14 pb-16 lg:pt-24 lg:pb-24 flex-1">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
                {t(lang, { en: "[00] Contact", fr: "[00] Nous joindre" })}
              </span>
              <span className="h-px w-16 bg-gray-300" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Vancouver · Canada</span>
            </div>
            <h1 className="headline text-4xl sm:text-5xl lg:text-6xl text-[#0a0a0a]">
              {t(lang, {
                en: <>Talk to <span className="text-orange-500">Routa</span>.</>,
                fr: <>Parlez à <span className="text-orange-500">Routa</span>.</>,
              })}
            </h1>
            <p className="mt-6 text-base text-gray-500 max-w-md leading-relaxed">
              {t(lang, {
                en: "Questions about a plan, coverage, integrations, or partnering with Routa? Send us a note and we'll come back to you within one business day.",
                fr: "Questions sur un forfait, la couverture, les intégrations ou un partenariat avec Routa? Écrivez-nous et nous vous répondrons en un jour ouvrable.",
              })}
            </p>
            <div className="mt-10 space-y-3 text-sm text-gray-600">
              <div><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 block mb-1">
                {t(lang, { en: "Ops mailbox", fr: "Boîte des opérations" })}
              </span>damiwilliams@routa.ca</div>
              <div><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 block mb-1">
                {t(lang, { en: "Headquarters", fr: "Siège social" })}
              </span>{t(lang, { en: "Vancouver, British Columbia · Canada", fr: "Vancouver, Colombie-Britannique · Canada" })}</div>
              <div>
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 block mb-1">
                  {t(lang, { en: "Common questions?", fr: "Questions fréquentes?" })}
                </span>
                <Link to={withLang("/faq", lang)} className="text-orange-500 hover:underline">
                  {t(lang, { en: "Check the FAQ →", fr: "Consulter la FAQ →" })}
                </Link>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 p-6 lg:p-10">
            {state === "sent" ? (
              <div className="text-center py-14" data-testid="contact-success">
                <CheckCircle2 className="mx-auto h-10 w-10 text-orange-500 mb-4" />
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-3">
                  {t(lang, { en: "[MESSAGE SENT]", fr: "[MESSAGE ENVOYÉ]" })}
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-[#0a0a0a]">
                  {t(lang, { en: "We got it.", fr: "Message bien reçu." })}
                </h2>
                <p className="mt-3 text-sm text-gray-500 max-w-sm mx-auto">
                  {t(lang, {
                    en: `Thanks ${form.name.split(" ")[0] || "for reaching out"} — someone on the ops team will reply to ${form.email} within one business day.`,
                    fr: `Merci ${form.name.split(" ")[0] || "de nous écrire"} — l'équipe des opérations vous répondra à ${form.email} en un jour ouvrable.`,
                  })}
                </p>
                <Link to={withLang("/", lang)} className="mt-8 inline-block font-mono text-[11px] tracking-[0.14em] uppercase text-[#0a0a0a] underline">
                  {t(lang, { en: "Back to home", fr: "Retour à l'accueil" })}
                </Link>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={submit} data-testid="contact-form">
                <div>
                  <label className="routa-label">{t(lang, { en: "Name", fr: "Nom" })}</label>
                  <input required data-testid="contact-name" value={form.name} onChange={set("name")} className="routa-input" />
                </div>
                <div>
                  <label className="routa-label">{t(lang, { en: "Email", fr: "Courriel" })}</label>
                  <input required type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="routa-input" />
                </div>
                <div>
                  <label className="routa-label">{t(lang, { en: "Message", fr: "Message" })}</label>
                  <textarea required rows={6} minLength={10} data-testid="contact-message" value={form.message} onChange={set("message")} className="routa-input resize-none" />
                </div>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")}
                       aria-hidden="true"
                       style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }} />
                {err && <div className="text-sm text-orange-600" data-testid="contact-error">{err}</div>}
                <PrimaryButton orange arrow type="submit" disabled={state === "busy"} data-testid="contact-submit" className="w-full">
                  {state === "busy"
                    ? t(lang, { en: "Sending…", fr: "Envoi…" })
                    : t(lang, { en: "Send message", fr: "Envoyer le message" })}
                </PrimaryButton>
                <p className="text-xs text-gray-400">
                  {t(lang, {
                    en: "By sending, you agree we may reply to this email. We don't share it with anyone else.",
                    fr: "En envoyant, vous acceptez que nous répondions à ce courriel. Nous ne le partagerons avec personne d'autre.",
                  })}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
